import React, { useState, useEffect, useMemo, Component } from 'react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import { AgGridReact } from 'ag-grid-react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import '../pagination/pagination.css';
import { IoAddSharp } from 'react-icons/io5';
import Pagination from '../pagination/pagination';
import Select from 'react-select';
import WindowedSelect from 'react-windowed-select';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { RiRefreshLine } from 'react-icons/ri';
import { FaFileCircleExclamation } from 'react-icons/fa6';
import { toast } from 'react-toastify';
import NoAccess from '../../../Routes/noAccess';
import api from '../../services/leaseGeneralInvoiceReceiptApi';
import { fmt3 } from './allocationHelpers';
import { getSelectedFinancialYear, selectStyles, toApiDate } from './formUtils';

const STORAGE_KEY = 'leaseGeneralInvoiceReceiptListState.v2';
const BASE = '/Lease/Management/Lease/GeneralInvoiceReceipt';
const FORM_NAME = 'Lease General Invoice Receipt';

const emptyFilters = () => ({
    voucherNo: '',
    customer: null,
    paymentType: null,
    // Default to the current month, e.g. 01 Sep to 30 Sep
    fromDate: moment().startOf('month').toDate(),
    toDate: moment().endOf('month').toDate(),
});

// Read synchronously so "no access" is not flashed before the effect runs.
const readPermission = (formName) => {
    try {
        const stored = JSON.parse(localStorage.getItem('RoleAccess')) || [];
        return stored.find((acc) => acc.formName === formName);
    } catch (error) {
        return undefined;
    }
};

const loadPersistedState = () => {
    try {
        const raw = JSON.parse(sessionStorage.getItem(STORAGE_KEY));
        if (!raw) return null;
        return {
            page: raw.page || 1,
            filters: {
                voucherNo: raw.voucherNo || '',
                customer: raw.customerNo ? { value: { value: raw.customerNo, text: raw.customerLabel }, label: `${raw.customerNo} - ${raw.customerLabel}` } : null,
                paymentType: raw.paymentType ? { value: { value: raw.paymentType, text: raw.paymentType }, label: raw.paymentType } : null,
                fromDate: raw.fromDate ? moment(raw.fromDate).toDate() : emptyFilters().fromDate,
                toDate: raw.toDate ? moment(raw.toDate).toDate() : emptyFilters().toDate,
            },
        };
    } catch (error) {
        return null;
    }
};

const persistState = (page, filters) => {
    try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
            page,
            voucherNo: filters?.voucherNo || '',
            customerNo: filters?.customer?.value?.value || '',
            customerLabel: filters?.customer?.value?.text || '',
            paymentType: filters?.paymentType?.value?.value || '',
            fromDate: toApiDate(filters?.fromDate) || '',
            toDate: toApiDate(filters?.toDate) || '',
        }));
    } catch (error) {
        // ignore storage failures
    }
};

const LeaseGeneralInvoiceReceiptList = () => {
    const navigate = useNavigate();
    const persisted = loadPersistedState();

    const [permissions] = useState(() => readPermission(FORM_NAME));
    const [FilterTab, setFilterTab] = useState(true);
    const [loading, setLoading] = useState(true);
    const [rowData, setRowData] = useState([]);
    const [pagination, setPagination] = useState();
    const [pageSize, setPageSize] = useState(50);
    const [contextMenu, setContextMenu] = useState(null);

    const [customerList, setCustomerList] = useState([]);
    const [paymentTypeList, setPaymentTypeList] = useState([]);
    const [financialYear] = useState(getSelectedFinancialYear());
    const [filters, setFilters] = useState(persisted?.filters || emptyFilters());

    useEffect(() => {
        const handleClickOutside = () => setContextMenu(null);
        window.addEventListener('click', handleClickOutside);
        return () => window.removeEventListener('click', handleClickOutside);
    }, []);

    // Runs on every mount, so returning from the form after a save or delete refetches the list.
    useEffect(() => {
        fetchDropdownData();
        getItems(persisted?.page || 1, persisted?.filters || emptyFilters());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchDropdownData = async () => {
        const res = await api.getDropdowns(['Customers', 'PaymentTypes']);
        if (!res.ok) {
            toast.error(res.message);
            return;
        }
        setCustomerList(res.dropdowns?.Customers || []);
        setPaymentTypeList(res.dropdowns?.PaymentTypes || []);
    };

    const getItems = async (newPageNo = pagination?.currentPage || 1, currentFilters = filters) => {
        setLoading(true);
        const res = await api.getReceipts({
            voucherNo: currentFilters?.voucherNo?.trim(),
            customerNo: currentFilters?.customer?.value?.value,
            paymentType: currentFilters?.paymentType?.value?.value,
            fromDate: toApiDate(currentFilters?.fromDate),
            toDate: toApiDate(currentFilters?.toDate),
            financialYear,
            pageNo: newPageNo || 1,
            pageSize: pageSize || 50,
        });
        setLoading(false);
        if (res.status === 204) {
            setRowData([]);
            setPagination({ currentPage: 1, totalPages: 1, totalRecords: 0 });
        } else if (res.ok) {
            setRowData(res.result || []);
            setPagination(res.pagination);
        } else {
            toast.error(res.message);
            return;
        }
        persistState(newPageNo, currentFilters);
    };

    const handleSearch = () => getItems(1, filters);

    const handleReset = () => {
        const cleared = emptyFilters();
        setFilters(cleared);
        getItems(1, cleared);
    };

    const handleFilterChange = (key, value) => setFilters((prev) => ({ ...prev, [key]: value }));

    const handleNew = () => navigate(`${BASE}/create`);
    const handleView = (row) => navigate(`${BASE}/view/${row?.rdNo}`);
    const handleEdit = (row) => navigate(`${BASE}/edit/${row?.rdNo}`);

    const handleMenuAction = (action, row) => {
        if (action === 'view') handleView(row);
        else if (action === 'edit') handleEdit(row);
        setContextMenu(null);
    };

    const dateFormatter = (params) => (params.value ? moment(params.value).format('DD-MMM-YYYY') : '');
    const amountFormatter = (params) => (params.value === null || params.value === undefined ? '' : fmt3(params.value));

    const columnDefs = useMemo(() => [
        { headerName: '', field: 'rdNo', cellRenderer: BtnCellRenderer, pinned: 'left', width: 40, suppressSizeToFit: true },
        { headerName: 'Voucher No', field: 'voucherNo', filter: true, floatingFilter: true, suppressMovable: true, width: 140 },
        { headerName: 'RD No', field: 'rdNo', filter: true, floatingFilter: true, suppressMovable: true, width: 110 },
        { headerName: 'Date', field: 'voucherDate', filter: true, floatingFilter: true, valueFormatter: dateFormatter, suppressMovable: true, width: 130 },
        { headerName: 'Customer', field: 'customerName', filter: true, floatingFilter: true, suppressMovable: true, width: 260 },
        { headerName: 'Payment Type', field: 'paymentType', filter: true, floatingFilter: true, suppressMovable: true, width: 150 },
        {
            headerName: 'Amount', field: 'amount', filter: true, floatingFilter: true, suppressMovable: true, width: 130,
            valueFormatter: amountFormatter, cellStyle: { textAlign: 'right' },
        },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    ], []);

    return (
        <div className="p-3">
            {permissions?.canView === true ? (
                <>
                    <div className="cost-center-section">
                        <div className="d-flex mt-3 cursor-pointer slide-header" onClick={() => setFilterTab(!FilterTab)}>
                            <div className="param-label pt-1">Search Parameters</div>
                        </div>
                        <div className={`slide-content ${FilterTab ? 'open' : 'closed'}`}>
                            <div className={`border-box2 p-3 ${FilterTab ? 'slide-in' : 'slide-out'}`}>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '15px' }}>
                                    <div>
                                        <label className="form-label">Voucher No</label>
                                        <input
                                            type="text"
                                            className="p-2 form-control"
                                            placeholder="Voucher No"
                                            value={filters.voucherNo}
                                            onChange={(e) => handleFilterChange('voucherNo', e.target.value)}
                                        />
                                    </div>
                                    <div>
                                        <label className="form-label">Customer</label>
                                        <WindowedSelect
                                            className="p-0"
                                            theme={(theme) => ({ ...theme, borderRadius: 5, padding: 9 })}
                                            menuPortalTarget={document.body}
                                            menuPlacement="auto"
                                            menuShouldScrollIntoView={false}
                                            styles={selectStyles}
                                            isClearable
                                            value={filters.customer}
                                            onChange={(opt) => handleFilterChange('customer', opt)}
                                            options={customerList?.map((rec) => ({ value: rec, label: `${rec?.value} - ${rec?.text}` }))}
                                        />
                                    </div>
                                    <div>
                                        <label className="form-label">Payment Type</label>
                                        <Select
                                            className="p-0"
                                            theme={(theme) => ({ ...theme, borderRadius: 5, padding: 9 })}
                                            menuPortalTarget={document.body}
                                            menuPlacement="auto"
                                            menuShouldScrollIntoView={false}
                                            styles={selectStyles}
                                            isClearable
                                            value={filters.paymentType}
                                            onChange={(opt) => handleFilterChange('paymentType', opt)}
                                            options={paymentTypeList?.map((rec) => ({ value: rec, label: rec?.text }))}
                                        />
                                    </div>
                                    <div>
                                        <label className="form-label">From Date</label>
                                        <DatePicker
                                            selected={filters.fromDate}
                                            onChange={(date) => handleFilterChange('fromDate', date)}
                                            dateFormat="dd-MMM-yyyy"
                                            className="p-2 form-control w-100"
                                            isClearable
                                        />
                                    </div>
                                    <div>
                                        <label className="form-label">To Date</label>
                                        <DatePicker
                                            selected={filters.toDate}
                                            onChange={(date) => handleFilterChange('toDate', date)}
                                            dateFormat="dd-MMM-yyyy"
                                            className="p-2 form-control w-100"
                                            isClearable
                                        />
                                    </div>
                                    <div className="d-flex gap-2 align-items-end">
                                        <div onClick={handleSearch} className="mainbtnGry flex-end gap-2 w-50">
                                            <FaFileCircleExclamation color="#fff" className="center mt-1 mx-2" />
                                            <div className="center">Search</div>
                                        </div>
                                        <div onClick={handleReset} className="mainbtnRed flex-end gap-2 w-50">
                                            <RiRefreshLine color="#fff" className="center mt-1 mx-2" />
                                            <div className="center">Reset</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="border-box2 p-3 mt-2">
                        {loading ? (
                            <div>Loading...</div>
                        ) : (
                            <>
                                <div className="d-flex justify-contant-between">
                                    <h5 className="col mainInvHead">Lease Billwise Receipt List</h5>
                                    <div className="d-flex justify-content-end gap-1">
                                        {permissions?.canAdd === true && (
                                            <div className="col center">
                                                <button className="mainbtn" type="button" onClick={handleNew}>
                                                    <IoAddSharp color="#fff" className="center mt-1" /> <div>New Receipt</div>
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="gridshad mt-2">
                                    <div className="ag-theme-quartz" style={{ height: 510 }}>
                                        <AgGridReact
                                            getRowStyle={(params) => (params.node.rowIndex % 2 === 0 ? { background: '#F0EDF5' } : { background: '#fff' })}
                                            rowData={rowData}
                                            columnDefs={columnDefs}
                                            enableFilter={true}
                                            floatingFilter={true}
                                            frameworkComponents={{ BtnCellRenderer }}
                                            context={{ setContextMenu }}
                                            onGridReady={(params) => params.api.sizeColumnsToFit()}
                                            onGridSizeChanged={(params) => params.api.sizeColumnsToFit()}
                                        />
                                    </div>
                                    <Pagination pageSize={pageSize} setPageSize={setPageSize} pagination={pagination} getItems={(page) => getItems(page, filters)} />
                                </div>
                            </>
                        )}
                    </div>
                    {contextMenu && (
                        <div
                            style={{
                                position: 'absolute',
                                top: contextMenu.mouseY,
                                left: contextMenu.mouseX,
                                backgroundColor: 'white',
                                border: '1px solid gray',
                                zIndex: 1000,
                                padding: '10px 30px',
                                borderRadius: '8px',
                                boxShadow: '0px 2px 6px rgba(0,0,0,0.2)',
                            }}
                        >
                            <div style={{ display: 'grid' }}>
                                <button className="btn btn-secondary" onClick={() => handleMenuAction('view', contextMenu.data)}>View</button>
                            </div>
                            <div style={{ display: 'grid' }}>
                                <button className="btn btn-secondary mt-2" onClick={() => handleMenuAction('edit', contextMenu.data)}>Edit</button>
                            </div>
                        </div>
                    )}
                </>
            ) : (
                <NoAccess />
            )}
        </div>
    );
};

class BtnCellRenderer extends Component {
    constructor(props) {
        super(props);
        this.handleClick = this.handleClick.bind(this);
    }

    handleClick(e) {
        e.stopPropagation();
        const { context, data } = this.props;
        if (context && context.setContextMenu) {
            context.setContextMenu({
                mouseX: e.clientX,
                mouseY: e.clientY,
                data,
            });
        }
    }

    render() {
        return (
            <div className="center pt-1">
                <span onClick={this.handleClick} style={{ cursor: 'pointer', fontSize: '30px' }}>
                    &#8942;
                </span>
            </div>
        );
    }
}

export default LeaseGeneralInvoiceReceiptList;
